/**
 * @file SalaryTypeListTable.jsx
 * @description Data table component for Salary Types.
 * It uses several custom hooks for pagination, searching, and table configuration.
 */

import React, { useState } from "react";
import { useRoleVerify } from "Common/RoleVerify";
import {
    GetAll,
    Delete,
} from "adminApi/SalaryTypeApi/SalaryType";
import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import useTableConfig from "Hooks/useTableConfig";
import { generateDisplayFields } from "utils/fieldMapper";
import { FilterStatus } from "utils/FilterStatus"
import fields from "../../json/SalaryType.json";

const SalaryTypeListTable = () => {
    // State management for pagination and search
    const [search, setSearch] = useState("");
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(1);

    // Debounce search input to optimize API calls
    const debouncedSearch = useDebouncedValue(search, 500);

    // Verify user roles for action buttons (Edit/Delete)
    const canShowAction = useRoleVerify("", ["can_edit", "can_delete"]);

    // Configuration options for page size entries
    const sizeOptions = [10, 20, 50, 100];
    const { tabledata } = fields;

    /**
     * usePaginatedFetch hook manages the server-side pagination state and API calls.
     */
    const { data, meta, loading, error, refetch } = usePaginatedFetch(GetAll, {
        page,
        limit,
        search: debouncedSearch,
        title: "Salary Type List",
    });

    /**
     * Map table column configuration from JSON to usable display fields.
     */
    const displayFields = generateDisplayFields(tabledata);

    // Apply status filtering logic (e.g., converting status 1/0 to text/badges)
    const filterdata = FilterStatus(data);

    /**
     * useTableConfig hook builds the columns and formatted rows for the DataTable component.
     */
    const { formattedData, columns } = useTableConfig({
        data: filterdata,
        page,
        limit,
        canShowAction,
        refetch,
        config: {
            entityName: "Salary Type",
            editUrl: "edit-salary-type",
            idKey: "salary_type_id",
            deleteApi: Delete,
            displayFields,
        },
    });

    /**
     * Handle search input changes and reset page if needed.
     */
    const handleSearch = (val, resetPage = null) => {
        setSearch(val);
        if (resetPage !== null) setPage(resetPage);
    };

    /**
     * Pagination change handler.
     */
    const onPageChange = (p) => {
        setPage(p);
    };

    /**
     * Limit (rows per page) change handler.
     */
    const onLimitChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1);
    };

    return (
        <DataTable
            columns={columns}
            data={formattedData}
            options={{
                loading,
                error,
                sortableColumns: displayFields.map((f) => f.accessor),
            }}
            Search={{
                search: true,
                SearchInputValue: search,
                handleSearch: handleSearch,
            }}
            Entries={{
                Entries: true,
                limit,
                onLimitChange,
                options: sizeOptions,
            }}
            pagination={{
                isActive: true,
                meta,
                onPageChange,
            }}
        />
    );
};

export default SalaryTypeListTable;

